import * as React from 'react';
import { RefObject, useEffect, useRef, useState } from 'react';

import { IconName } from '../../enums';
import { useOnClickOutside } from '../../hooks';
import { DropdownOption } from '../../interfaces';
import { InputLabelBaseComponent } from '../../theme';
import { UseState } from '../../types';

import {
  Container,
  DropdownOptionItem,
  DropdownOptions,
  InputContainer,
  StyledIcon,
  Value
} from './dropdown.styled';
import { DropdownProps } from './dropdown.types';

export const Dropdown: React.FC<DropdownProps> = (props: DropdownProps): JSX.Element => {
  const { label, options, initialValue, initialValueName, onChooseAnswer, disabled = false, className }: DropdownProps = props;
  const [optionsVisible, setOptionsVisible]: UseState<boolean> = useState<boolean>(false);
  const [selectedOption, setSelectedOption]: UseState<DropdownOption | null> = useState<DropdownOption | null>(initialValue ?? null);
  const containerRef: RefObject<HTMLDivElement> = useRef(null);
  useOnClickOutside<HTMLDivElement>([containerRef], (): void => setOptionsVisible(false));

  useEffect((): void => setSelectedOption(initialValue ?? null), [initialValue]);

  useEffect(
    (): void => {
      const item: DropdownOption | undefined = options.find((option: DropdownOption): boolean => option.name === initialValueName);
      setSelectedOption(item ?? null);
    },
    [initialValueName]
  );

  const onOptionClick: (option: DropdownOption) => void = (option: DropdownOption): void => {
    setSelectedOption(option);
    onChooseAnswer(option);
    setOptionsVisible(false);
  };

  const toggleOptionsList = (): void => {
    if (disabled) {
      setOptionsVisible(false);
      return;
    }

    setOptionsVisible(!optionsVisible);
  };

  return (
    <Container className={className} ref={containerRef}>
      <InputContainer onClick={toggleOptionsList} disabled={disabled} data-testid='input-container'>
        <InputLabelBaseComponent
          $isOnTop={!!selectedOption}
          $disabled={disabled}
          $hasSmallerWidth={!selectedOption}
        >
          {label}
        </InputLabelBaseComponent>
        <Value>
          {selectedOption?.label ?? ''}
        </Value>
        <StyledIcon name={IconName.ArrowDown15} size={20} />
      </InputContainer>
      <DropdownOptions $visible={optionsVisible}>
        {options.map((option: DropdownOption): JSX.Element => (
          <DropdownOptionItem key={option.name} onClick={(): void => onOptionClick(option)}>
            {option.label}
          </DropdownOptionItem>
        ))}
      </DropdownOptions>
    </Container>
  );
};
